import { useCallback, useEffect, useRef, type RefObject } from "react";
import { Form, FormikProvider, useFormik } from "formik";
import { DEFAULT_SCHEDULE_TEMPLATE_GROUP } from "@common/constants/domains/accounting/schedules";
import { Button } from "@design-system/Button/Button";
import { Field } from "@design-system/Field/Field";
import { HStack } from "@design-system/HStack/HStack";
import { Input } from "@design-system/Input/Input";
import { VStack } from "@design-system/VStack/VStack";
import { PlainModal, StandardModalHeader } from "@design-system/Modal/Modal";
import {
    DUPLICATE_SCHEDULE_GROUP_MESSAGE,
    isExistingScheduleGroupName,
} from "./scheduleGroupName";

interface NewScheduleGroupValues {
    group: string;
}

interface NewScheduleGroupModalProps {
    show: boolean;
    onHide: () => void;
    onCreate: (group: string) => void;
    existingGroupNames?: readonly string[];
}

const NewScheduleGroupForm = ({
    onHide,
    onCreate,
    existingGroupNames,
    nameInputRef,
    setNameInputRef,
}: Omit<NewScheduleGroupModalProps, "show"> & {
    nameInputRef: RefObject<HTMLInputElement | null>;
    setNameInputRef: (node: HTMLInputElement | null) => void;
}) => {
    useEffect(() => {
        const input = nameInputRef.current;
        if (input === null) {
            return;
        }
        // The picker returns focus to its trigger after the footer row closes
        // the popup. Focusing in this effect, then again in a microtask, lands
        // after that return.
        input.focus();
        queueMicrotask(() => {
            input.focus();
        });
    }, [nameInputRef]);

    const formik = useFormik<NewScheduleGroupValues>({
        initialValues: { group: "" },
        onSubmit: (values) => {
            const group = values.group.trim();
            if (
                group === "" ||
                (existingGroupNames !== undefined &&
                    isExistingScheduleGroupName(group, existingGroupNames))
            ) {
                return;
            }
            onCreate(group);
            onHide();
        },
    });

    const trimmedGroup = formik.values.group.trim();
    const isDuplicate =
        existingGroupNames !== undefined &&
        isExistingScheduleGroupName(trimmedGroup, existingGroupNames);

    return (
        <FormikProvider value={formik}>
            <Form>
                <VStack gap={16}>
                    <Field
                        label="Group name"
                        invalid={isDuplicate}
                        error={
                            isDuplicate
                                ? DUPLICATE_SCHEDULE_GROUP_MESSAGE
                                : undefined
                        }
                    >
                        <Input
                            ref={setNameInputRef}
                            name="group"
                            autoFocus
                            size="xl"
                            placeholder={DEFAULT_SCHEDULE_TEMPLATE_GROUP}
                            value={formik.values.group}
                            invalid={isDuplicate}
                            aria-required
                            onValueChange={(group) => {
                                void formik.setFieldValue("group", group);
                            }}
                        />
                    </Field>
                    <HStack as="footer" gap={8} justify="end">
                        <Button
                            type="button"
                            variant="secondary"
                            size="xl"
                            onClick={onHide}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="tertiary"
                            size="xl"
                            disabled={trimmedGroup === "" || isDuplicate}
                        >
                            Continue
                        </Button>
                    </HStack>
                </VStack>
            </Form>
        </FormikProvider>
    );
};

export const NewScheduleGroupModal: React.FC<NewScheduleGroupModalProps> = ({
    show,
    onHide,
    onCreate,
    existingGroupNames,
}) => {
    const nameInputRef = useRef<HTMLInputElement | null>(null);
    const setNameInputRef = useCallback((node: HTMLInputElement | null) => {
        nameInputRef.current = node;
    }, []);

    return (
        <PlainModal
            show={show}
            onHide={onHide}
            size="md"
            initialFocus={nameInputRef}
        >
            <StandardModalHeader>New group</StandardModalHeader>
            <NewScheduleGroupForm
                onHide={onHide}
                onCreate={onCreate}
                existingGroupNames={existingGroupNames}
                nameInputRef={nameInputRef}
                setNameInputRef={setNameInputRef}
            />
        </PlainModal>
    );
};
